import { produce } from "immer";
import { ActionType, createReducer } from "typesafe-actions";

import { EVAL_RUN_LOAD_ERROR, EVAL_RUNS_DEFAULT_LIMIT } from "../constants";
import { EvalRunsFilter, EvaluationStateType } from "../interfaces";
import * as actions from "./actions";

type Action = ActionType<typeof actions>;

const defaultFilter: EvalRunsFilter = {
  page: 0,
  limit: EVAL_RUNS_DEFAULT_LIMIT,
};

export const initialState: EvaluationStateType = {
  runs: [],
  runsTotal: 0,
  activeRun: null,
  datasets: [],
  filter: { ...defaultFilter },
  submitError: null,
  lastSubmittedUuid: null,
  loadError: null,
};

const reducer = createReducer<EvaluationStateType, Action>(initialState)
  .handleAction(actions.getEvalRuns.request, (state) =>
    produce(state, (nextState) => {
      nextState.loadError = null;
    }),
  )
  // ALWAYS replaces, unlike Chat's getChatList.success — Chat's list is an infinite-scroll sidebar and
  // appends past page 0; this list is a paginated rsuite table (Phase 20h), where page 2 must replace
  // page 1's rows rather than growing the array by a page on every fetch.
  .handleAction(actions.getEvalRuns.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.runs = [...action.payload.results];
      nextState.runsTotal = action.payload.total;
      nextState.loadError = null;
    }),
  )
  .handleAction(actions.getEvalRuns.failure, (state) =>
    produce(state, (nextState) => {
      nextState.loadError = EVAL_RUN_LOAD_ERROR;
    }),
  )
  .handleAction(actions.getEvalRun.request, (state) =>
    produce(state, (nextState) => {
      nextState.loadError = null;
    }),
  )
  .handleAction(actions.getEvalRun.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.activeRun = action.payload;
      nextState.loadError = null;
    }),
  )
  .handleAction(actions.getEvalRun.failure, (state) =>
    produce(state, (nextState) => {
      nextState.loadError = EVAL_RUN_LOAD_ERROR;
    }),
  )
  // Clears BOTH submitError and lastSubmittedUuid — the within-instance guarantee described on each
  // field's own doc comment. Neither clear is what stops a stale value from a PREVIOUS modal
  // instance; the modal's own hasSubmitted flag gates that.
  .handleAction(actions.submitEvalRun.request, (state) =>
    produce(state, (nextState) => {
      nextState.submitError = null;
      nextState.lastSubmittedUuid = null;
    }),
  )
  .handleAction(actions.submitEvalRun.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.submitError = null;
      nextState.lastSubmittedUuid = action.payload.uuid;
    }),
  )
  .handleAction(actions.submitEvalRun.failure, (state, action) =>
    produce(state, (nextState) => {
      nextState.submitError = action.payload;
    }),
  )
  .handleAction(actions.deleteEvalRun.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.runs = nextState.runs.filter((run) => run.uuid !== action.payload.uuid);
      nextState.runsTotal = nextState.runsTotal - 1;
      if (state.activeRun && state.activeRun.uuid === action.payload.uuid) {
        nextState.activeRun = null;
      }
    }),
  )
  .handleAction(actions.getEvalDatasets.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.datasets = action.payload;
    }),
  )
  .handleAction(actions.setFilter, (state, action) =>
    produce(state, (nextState) => {
      nextState.filter = action.payload || { ...defaultFilter };
    }),
  )
  .handleAction(actions.clearEvalRun, (state) =>
    produce(state, (nextState) => {
      nextState.activeRun = null;
    }),
  )
  .handleAction(actions.clearEvalRuns, (state) =>
    produce(state, (nextState) => {
      nextState.runs = [];
      nextState.runsTotal = 0;
    }),
  );

export { reducer as EvaluationReducer };
